import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { LoadingState, Section } from '../components/public/ui';

const BeltDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [belt, setBelt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('');
  const [openPrecursor, setOpenPrecursor] = useState(null);

  useEffect(() => {
    const fetchBeltDetail = async () => {
      try {
        const response = await api.get(`/public/belts/${id}`);
        setBelt(response.data.data);
      } catch (error) {
        console.error('Error fetching belt detail:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBeltDetail();
  }, [id]);

  const hasWeightTables = Boolean(belt?.weight_tables?.length);
  const hasPrecursors = Boolean(belt?.precursors?.length);

  const tabs = useMemo(() => {
    const list = [
      { id: 'requirements', label: 'Requisitos' },
      { id: 'theory', label: 'Teoría' },
    ];
    if (hasPrecursors) list.push({ id: 'precursors', label: 'Precursores' });
    if (hasWeightTables) list.push({ id: 'weights', label: 'Pesos' });
    return list;
  }, [hasPrecursors, hasWeightTables]);

  const getVideoEmbedUrl = (url) => {
    if (!url) return '';
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.split('v=')[1]?.split('&')[0] || url.split('/').pop();
      return `https://www.youtube.com/embed/${videoId}`;
    }
    if (url.includes('drive.google.com')) {
      const fileId =
        url.match(/\/d\/([^/]+)/)?.[1] || url.match(/\/file\/d\/([^/]+)/)?.[1];
      if (fileId) return `https://drive.google.com/file/d/${fileId}/preview`;
    }
    return url;
  };

  const handleOpenVideo = (videoUrl) => {
    setSelectedVideoUrl(videoUrl);
    setVideoModalOpen(true);
  };

  if (loading) return <LoadingState />;

  if (!belt) {
    return (
      <Section className="section-pad text-center">
        <p className="font-display text-2xl font-semibold uppercase text-primary">
          Cinturón no encontrado
        </p>
        <button type="button" className="btn-secondary mt-6" onClick={() => navigate('/belts')}>
          Volver a cinturones
        </button>
      </Section>
    );
  }

  const activeTab = tabs[tabValue]?.id;

  return (
    <Section className="section-pad">
      <button type="button" className="btn-ghost mb-6 !px-0" onClick={() => navigate('/belts')}>
        ← Volver a cinturones
      </button>

      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div
          className="h-14 w-14 shrink-0 rounded-full shadow-md"
          style={{ backgroundColor: belt.color_code || '#000' }}
          aria-hidden="true"
        />
        <div>
          <h1 className="page-title">Cinturón {belt.name}</h1>
          <span className="mt-2 inline-flex rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
            Nivel {belt.hierarchy_order}
          </span>
        </div>
      </div>

      {belt.description && <p className="max-w-3xl text-muted">{belt.description}</p>}

      {belt.youtube_video_url && (
        <a
          href={belt.youtube_video_url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary mt-6"
        >
          Ver video técnico
        </a>
      )}

      <div className="mt-10 overflow-hidden rounded-lg border border-line bg-surface">
        <div
          className="flex gap-1 overflow-x-auto border-b border-line p-2"
          role="tablist"
          aria-label="Detalle del cinturón"
        >
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={tabValue === index}
              className={`min-h-11 shrink-0 cursor-pointer rounded-md px-4 text-sm font-semibold transition-colors duration-200 ${
                tabValue === index
                  ? 'bg-primary text-white'
                  : 'text-ink hover:bg-canvas'
              }`}
              onClick={() => setTabValue(index)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-5 md:p-8">
          {activeTab === 'requirements' && (
            <div>
              <h2 className="font-display text-2xl font-semibold uppercase tracking-wide">
                Requisitos del examen
              </h2>
              {belt.requirements?.length ? (
                <ul className="mt-6 divide-y divide-line">
                  {belt.requirements.map((req) => (
                    <li key={req.id} className="py-5">
                      <div className="flex items-start gap-3">
                        <span className="inline-flex min-w-8 items-center justify-center rounded-md bg-primary px-2 py-1 text-sm font-bold text-white">
                          {req.requirement_number}
                        </span>
                        <div>
                          <h3 className="font-semibold text-ink">{req.title}</h3>
                          {req.description && (
                            <p className="mt-1 text-sm text-muted">{req.description}</p>
                          )}
                          {req.notes && (
                            <p className="mt-2 text-sm italic text-muted">{req.notes}</p>
                          )}
                          {req.video_url && (
                            <button
                              type="button"
                              className="btn-primary mt-3 !min-h-10 !px-4 !py-2 text-sm"
                              onClick={() => handleOpenVideo(req.video_url)}
                            >
                              Ver video
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-muted">No hay requisitos disponibles para este cinturón.</p>
              )}
              <div className="mt-6 rounded-md bg-canvas p-4 text-sm text-muted">
                <strong className="text-ink">Nota:</strong> Puntaje mínimo de aprobación: 60 puntos
                – Máximo 100 puntos (6 como mínimo en cada punto)
              </div>
            </div>
          )}

          {activeTab === 'theory' && (
            <div>
              <h2 className="font-display text-2xl font-semibold uppercase tracking-wide">
                Cuestionario teórico
              </h2>
              {belt.theory_questions?.length ? (
                <ul className="mt-6 space-y-6">
                  {belt.theory_questions.map((question) => (
                    <li key={question.id} className="rounded-md border border-line p-4">
                      <div className="flex gap-3">
                        <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-full bg-ink text-xs font-bold text-white">
                          {question.question_number}
                        </span>
                        <p className="font-semibold">{question.question_text}</p>
                      </div>
                      <div className="mt-3 border-l-4 border-primary bg-primary/5 p-3 text-sm">
                        <strong className="text-primary">Respuesta:</strong>{' '}
                        <span className="text-ink">{question.answer_text}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-muted">
                  No hay preguntas teóricas disponibles para este cinturón.
                </p>
              )}
              <div className="mt-6 rounded-md bg-canvas p-4 text-sm text-muted">
                <strong className="text-ink">Importante:</strong> Estudia bien cada pregunta y
                respuesta. El examen teórico es parte fundamental de la evaluación.
              </div>
            </div>
          )}

          {activeTab === 'precursors' && (
            <div>
              <h2 className="font-display text-2xl font-semibold uppercase tracking-wide">
                Precursores
              </h2>
              {belt.precursors?.length ? (
                <ul className="mt-6 space-y-3">
                  {belt.precursors.map((precursor) => {
                    const open = openPrecursor === precursor.id;
                    return (
                      <li key={precursor.id} className="overflow-hidden rounded-md border border-line">
                        <button
                          type="button"
                          className="flex min-h-14 w-full cursor-pointer items-center gap-3 px-4 py-3 text-left hover:bg-canvas"
                          aria-expanded={open}
                          onClick={() =>
                            setOpenPrecursor(open ? null : precursor.id)
                          }
                        >
                          {precursor.photo_url ? (
                            <img
                              src={precursor.photo_url}
                              alt=""
                              className="h-12 w-12 rounded-full object-cover"
                            />
                          ) : (
                            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 font-display text-lg text-primary">
                              {precursor.name?.[0] || '?'}
                            </span>
                          )}
                          <span className="font-display text-xl font-semibold uppercase tracking-wide">
                            {precursor.name}
                          </span>
                        </button>
                        {open && (
                          <div className="border-t border-line px-4 py-4 text-sm leading-relaxed text-muted whitespace-pre-line">
                            {precursor.biography}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="mt-4 text-muted">No hay precursores disponibles.</p>
              )}
            </div>
          )}

          {activeTab === 'weights' && (
            <div>
              <h2 className="font-display text-2xl font-semibold uppercase tracking-wide">
                Categorías de peso
              </h2>
              {belt.weight_tables?.length ? (
                <div className="mt-6 space-y-10">
                  {belt.weight_tables.map((table) => (
                    <div key={table.id}>
                      <h3 className="mb-3 text-center font-display text-lg font-bold uppercase tracking-wide text-primary">
                        {table.title}
                      </h3>
                      <div className="overflow-x-auto rounded-md border border-line bg-surface">
                        <table className="min-w-full text-sm text-ink">
                          <thead>
                            <tr className="border-b border-line bg-canvas">
                              <th className="px-4 py-3 text-center font-semibold text-ink">
                                {table.column_boys}
                              </th>
                              <th className="px-4 py-3 text-center font-semibold text-ink">
                                {table.column_girls}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {table.rows?.length ? (
                              table.rows.map((row) => (
                                <tr
                                  key={row.id}
                                  className="border-b border-line odd:bg-surface even:bg-canvas"
                                >
                                  <td className="px-4 py-3 text-center font-medium text-ink">
                                    {row.weight_boys}
                                  </td>
                                  <td className="px-4 py-3 text-center font-medium text-ink">
                                    {row.weight_girls}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={2} className="px-4 py-6 text-center text-muted">
                                  No hay datos disponibles
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="mt-4 text-muted">No hay tablas de peso disponibles.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {videoModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Video demostrativo"
          onClick={() => {
            setVideoModalOpen(false);
            setSelectedVideoUrl('');
          }}
        >
          <div
            className="w-full max-w-3xl overflow-hidden rounded-lg bg-surface"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <h2 className="font-display text-xl font-semibold uppercase">Video demostrativo</h2>
              <button
                type="button"
                className="btn-ghost !min-h-10"
                aria-label="Cerrar"
                onClick={() => {
                  setVideoModalOpen(false);
                  setSelectedVideoUrl('');
                }}
              >
                ✕
              </button>
            </div>
            <div className="relative aspect-video bg-black">
              {selectedVideoUrl && (
                <iframe
                  src={getVideoEmbedUrl(selectedVideoUrl)}
                  title="Video demostrativo"
                  className="absolute inset-0 h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
          </div>
        </div>
      )}
    </Section>
  );
};

export default BeltDetail;
